import React, { useMemo, useState } from 'react';
import { Award, AlertTriangle, CheckCircle2, XCircle, BarChart3, ChevronDown, ChevronRight, BookOpen, LayoutList, Info, Eye, EyeOff, TrendingUp } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
import { buildQualityReport, QuestionQualityScore, QualityDimension, QuestionQualityReport } from '../../../governance/questionQualityEngine';
type FlagFilter = 'all' | 'generic-feedback' | 'no-explanation' | 'weak-distractor' | 'rule-violation' | 'tier-mismatch' | 'poor' | 'excellent';
export function QuestionQualityPanel({
  report: reportProp
}: {
  report?: QuestionQualityReport;
}) {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  // NEW-BUG-2 fix: use pre-computed report from QualityWorkspace if provided,
  // otherwise fall back to computing from filteredRecords (e.g. when used standalone)
  const report = useMemo(() => {
    if (reportProp) return reportProp;
    const questions = filteredRecords.map(r => r.question);
    return buildQualityReport(questions);
  }, [reportProp, filteredRecords]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  const [showExplanationPreview, setShowExplanationPreview] = useState(false);
  const filteredScores = useMemo(() => {
    let scores = [...report.scores];
    switch (flagFilter) {
      case 'generic-feedback':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Generic feedback'));
        break;
      case 'no-explanation':
        scores = scores.filter(s => s.flags.some(f => f.label === 'No explanation'));
        break;
      case 'weak-distractor':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Weak distractor' || f.label === 'Generic distractor'));
        break;
      case 'rule-violation':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Rule violation'));
        break;
      case 'tier-mismatch':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Tier mismatch' || f.label === 'Tier drift'));
        break;
      case 'poor':
        scores = scores.filter(s => s.grade === 'poor');
        break;
      case 'excellent':
        scores = scores.filter(s => s.grade === 'excellent');
        break;
    }
    return scores.sort((a, b) => sortOrder === 'asc' ? a.totalScore - b.totalScore : b.totalScore - a.totalScore);
  }, [report.scores, sortOrder, flagFilter]);
  // Flag counts for filter buttons
  const flagCounts = useMemo(() => ({
    genericFeedback: report.scores.filter(s => s.flags.some(f => f.label === 'Generic feedback')).length,
    noExplanation: report.scores.filter(s => s.flags.some(f => f.label === 'No explanation')).length,
    weakDistractor: report.scores.filter(s => s.flags.some(f => f.label === 'Weak distractor' || f.label === 'Generic distractor')).length,
    ruleViolation: report.scores.filter(s => s.flags.some(f => f.label === 'Rule violation')).length,
    tierMismatch: report.scores.filter(s => s.flags.some(f => f.label === 'Tier mismatch' || f.label === 'Tier drift')).length,
    poor: report.scores.filter(s => s.grade === 'poor').length,
    excellent: report.scores.filter(s => s.grade === 'excellent').length
  }), [report.scores]);
  return <div className="space-y-6" data-id="element-2501">
      {/* Summary Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2502">
        <Card className="p-4 flex flex-col justify-between bg-slate-50 border-slate-200" data-id="element-2503">
          <div data-id="element-2504">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2505">
              Average Quality Score
            </p>
            <div className="mt-2 flex items-baseline gap-2" data-id="element-2506">
              <span className={`text-3xl font-black ${getScoreColor(report.avgScore)}`} data-id="element-2507">
                {report.avgScore}
              </span>
              <span className="text-sm text-slate-400" data-id="element-2508">/ 100</span>
            </div>
          </div>
          <div className="mt-4 h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-2509">
            <div className={`h-full ${getScoreBgColor(report.avgScore)}`} style={{
            width: `${report.avgScore}%`
          }} data-id="element-2510" />
          </div>
        </Card>

        <Card className="p-4 col-span-1 md:col-span-3" data-id="element-2511">
          <div className="flex items-center justify-between mb-4" data-id="element-2512">
            <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2513">
              <BarChart3 className="w-4 h-4 text-indigo-500" data-id="element-2514" />
              Quality Distribution
            </h3>
            <div className="flex gap-2 text-xs flex-wrap" data-id="element-2515">
              <Badge variant="success" size="sm" data-id="element-2516">
                {report.distribution.excellent} Excellent
              </Badge>
              <Badge variant="info" size="sm" data-id="element-2517">
                {report.distribution.good} Good
              </Badge>
              <Badge variant="warning" size="sm" data-id="element-2518">
                {report.distribution.needsWork} Needs Work
              </Badge>
              <Badge variant="danger" size="sm" data-id="element-2519">
                {report.distribution.poor} Poor
              </Badge>
            </div>
          </div>
          <div className="flex h-8 w-full rounded-lg overflow-hidden" data-id="element-2520">
            {report.totalQuestions > 0 ? <>
                <div style={{
              width: `${report.distribution.excellent / report.totalQuestions * 100}%`
            }} className="bg-emerald-500 h-full" title="Excellent" data-id="element-2521" />
                <div style={{
              width: `${report.distribution.good / report.totalQuestions * 100}%`
            }} className="bg-blue-500 h-full" title="Good" data-id="element-2522" />
                <div style={{
              width: `${report.distribution.needsWork / report.totalQuestions * 100}%`
            }} className="bg-amber-500 h-full" title="Needs Work" data-id="element-2523" />
                <div style={{
              width: `${report.distribution.poor / report.totalQuestions * 100}%`
            }} className="bg-rose-500 h-full" title="Poor" data-id="element-2524" />
              </> : <div className="w-full h-full bg-slate-100 flex items-center justify-center text-xs text-slate-400" data-id="element-2525">
                No data
              </div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2526">
        {/* Left Column */}
        <div className="space-y-6" data-id="element-2527">
          {/* Module Breakdown */}
          <Card className="p-0 overflow-hidden" data-id="element-2528">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-2529">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2530">
                <LayoutList className="w-4 h-4 text-slate-500" data-id="element-2531" />
                Module Breakdown
              </h3>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-2532">
              {report.byModule.map(mod => {
              const grade = mod.avgScore >= 80 ? 'excellent' : mod.avgScore >= 60 ? 'good' : mod.avgScore >= 40 ? 'needs-work' : 'poor';
              return <div key={mod.module} className="p-3" data-id="element-2533">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-2534">
                      <div data-id="element-2535">
                        <div className="font-medium text-slate-900 text-sm" data-id="element-2536">
                          {mod.module}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-2537">
                          {mod.questionCount} questions
                        </div>
                      </div>
                      <Badge variant={getGradeVariant(grade)} data-id="element-2538">
                        Avg: {mod.avgScore}
                      </Badge>
                    </div>
                    {/* Mini score bar */}
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2539">
                      <div className={`h-full rounded-full ${getScoreBgColor(mod.avgScore)}`} style={{
                    width: `${mod.avgScore}%`
                  }} data-id="element-2540" />
                    </div>
                    {/* Top issues for this module */}
                    {mod.topIssues.length > 0 && <div className="mt-2 space-y-0.5" data-id="element-2541">
                        {mod.topIssues.slice(0, 2).map((issue, idx) => <div key={idx} className="text-xs text-slate-500 flex items-center gap-1 truncate" data-id="element-2542">
                            <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" data-id="element-2543" />
                            <span className="truncate" data-id="element-2544">{issue.issue}</span>
                            <span className="shrink-0 text-slate-400" data-id="element-2545">
                              ×{issue.count}
                            </span>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>

          {/* Top Issues */}
          <Card className="p-0 overflow-hidden" data-id="element-2546">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-2547">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2548">
                <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-2549" />
                Top Quality Issues
              </h3>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-2550">
              {report.topIssues.length > 0 ? report.topIssues.map((issue, idx) => <div key={idx} className="p-3" data-id="element-2551">
                    <div className="flex justify-between items-start gap-2" data-id="element-2552">
                      <span className="text-xs text-slate-700 font-medium leading-snug" data-id="element-2553">
                        {issue.issue}
                      </span>
                      <Badge variant="neutral" size="sm" className="shrink-0" data-id="element-2554">
                        {issue.count}
                      </Badge>
                    </div>
                    <div className="mt-1" data-id="element-2555">
                      <Badge variant={issue.severity === 'critical' ? 'danger' : issue.severity === 'warning' ? 'warning' : 'info'} size="sm" data-id="element-2556">
                        {issue.severity}
                      </Badge>
                    </div>
                  </div>) : <div className="p-4 text-center text-sm text-slate-500" data-id="element-2557">
                  No common issues found
                </div>}
            </div>
          </Card>
        </div>

        {/* Right Column: Question List */}
        <div className="lg:col-span-2 space-y-4" data-id="element-2558">
          <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-2559">
            <h3 className="font-bold text-slate-900 text-lg" data-id="element-2560">
              Question Analysis
            </h3>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2561">
              <button onClick={() => setShowExplanationPreview(v => !v)} className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white transition-colors" data-id="element-2562">
                {showExplanationPreview ? <EyeOff className="w-3.5 h-3.5" data-id="element-2563" /> : <Eye className="w-3.5 h-3.5" data-id="element-2564" />}
                {showExplanationPreview ? 'Hide' : 'Show'} Explanation
              </button>
              <div className="flex bg-white rounded-lg border border-slate-200 p-0.5" data-id="element-2565">
                <button onClick={() => setSortOrder('asc')} className={`px-2 py-1 text-xs font-medium rounded ${sortOrder === 'asc' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2566">
                  Lowest First
                </button>
                <button onClick={() => setSortOrder('desc')} className={`px-2 py-1 text-xs font-medium rounded ${sortOrder === 'desc' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2567">
                  Highest First
                </button>
              </div>
            </div>
          </div>

          {/* Flag Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-2568">
            {([{
            id: 'all',
            label: 'All',
            count: report.scores.length
          }, {
            id: 'generic-feedback',
            label: '⚠ Generic Feedback',
            count: flagCounts.genericFeedback
          }, {
            id: 'no-explanation',
            label: '✗ No Explanation',
            count: flagCounts.noExplanation
          }, {
            id: 'weak-distractor',
            label: '⚠ Weak Distractor',
            count: flagCounts.weakDistractor
          }, {
            id: 'rule-violation',
            label: '✗ Rule Violation',
            count: flagCounts.ruleViolation
          }, {
            id: 'tier-mismatch',
            label: '📊 Tier Drift',
            count: flagCounts.tierMismatch
          }, {
            id: 'poor',
            label: '🔴 Poor',
            count: flagCounts.poor
          }, {
            id: 'excellent',
            label: '🟢 Excellent',
            count: flagCounts.excellent
          }] as Array<{
            id: FlagFilter;
            label: string;
            count: number;
          }>).map(f => <button key={f.id} onClick={() => setFlagFilter(f.id)} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${flagFilter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`} data-id="element-2569">
                {f.label}
                <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full ${flagFilter === f.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-2570">
                  {f.count}
                </span>
              </button>)}
          </div>

          <div className="space-y-3" data-id="element-2571">
            {filteredScores.slice(0, 50).map(score => <QuestionScoreCard key={score.questionId} score={score} isExpanded={expandedQuestionId === score.questionId} showExplanationPreview={showExplanationPreview} onToggle={() => setExpandedQuestionId(expandedQuestionId === score.questionId ? null : score.questionId)} data-id="element-2572" />)}
            {filteredScores.length === 0 && <Card className="p-8 text-center" data-id="element-2573">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" data-id="element-2574" />
                <p className="font-bold text-slate-900" data-id="element-2575">
                  No questions match this filter
                </p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2576">
                  Try a different flag filter or clear the selection.
                </p>
              </Card>}
            {filteredScores.length > 50 && <div className="text-center py-4 text-sm text-slate-500" data-id="element-2577">
                Showing 50 of {filteredScores.length} questions. Use filters to
                narrow results.
              </div>}
          </div>
        </div>
      </div>
    </div>;
}
function QuestionScoreCard({
  score,
  isExpanded,
  onToggle,
  showExplanationPreview
}: {
  score: QuestionQualityScore;
  isExpanded: boolean;
  onToggle: () => void;
  showExplanationPreview: boolean;
}) {
  const hasGenericFeedback = score.flags.some(f => f.label === 'Generic feedback');
  const hasGenericDistractor = score.flags.some(f => f.label === 'Generic distractor');
  return <Card className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-indigo-500 shadow-md' : hasGenericFeedback ? 'ring-1 ring-rose-300 bg-rose-50/30' : 'hover:shadow-sm'}`} data-id="element-2578">
      {hasGenericFeedback && !isExpanded && <div className="px-4 pt-3 pb-0" data-id="element-2579">
          <div className="flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-100 border border-rose-200 rounded-lg px-3 py-1.5" data-id="element-2580">
            <XCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2581" />
            Boilerplate feedback — does not explain the concept or calculation
          </div>
        </div>}

      <div className="p-4 cursor-pointer flex items-start gap-4" onClick={onToggle} data-id="element-2582">
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg ${getScoreBgColor(score.totalScore)} text-white`} data-id="element-2583">
          {score.totalScore}
        </div>

        <div className="flex-1 min-w-0" data-id="element-2584">
          <div className="flex items-center justify-between mb-1" data-id="element-2585">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2586">
              <code className="text-sm font-bold text-slate-900" data-id="element-2587">
                {score.questionId}
              </code>
              <Badge variant="outline" size="sm" data-id="element-2588">
                {score.module}
              </Badge>
              <Badge variant="outline" size="sm" data-id="element-2589">
                {score.levelCode}
              </Badge>
            </div>
            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2590" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2591" />}
          </div>

          <div className="flex flex-wrap gap-2 mt-2" data-id="element-2592">
            {score.flags.map((flag, idx) => <Badge key={idx} variant={flag.type === 'critical' ? 'danger' : flag.type === 'warning' ? 'warning' : 'success'} size="sm" data-id="element-2593">
                {flag.label}
              </Badge>)}
            {score.flags.length === 0 && <span className="text-xs text-slate-400 italic" data-id="element-2594">
                No major flags
              </span>}
          </div>

          {/* Explanation preview */}
          {showExplanationPreview && <div className={`mt-2 text-xs rounded-lg px-3 py-2 border ${hasGenericFeedback ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-2595">
              <span className="font-semibold" data-id="element-2596">Explanation: </span>
              {score.explanationText ? <span className={`line-clamp-2 ${hasGenericFeedback ? 'italic' : ''}`} data-id="element-2597">
                  {score.explanationText}
                </span> : <span className="italic opacity-60" data-id="element-2598">[No explanation text]</span>}
            </div>}
        </div>

        {/* Mini Dimension Bars - UPDATED for new scoring model */}
        <div className="hidden lg:flex gap-1 items-end h-10 w-36 pb-1" data-id="element-2599">
          <DimensionBar score={score.dimensions.explanationClarity} label="Clarity" data-id="element-2600" />
          <DimensionBar score={score.dimensions.didacticDepth} label="Depth" data-id="element-2601" />
          <DimensionBar score={score.dimensions.distractors} label="Dist" data-id="element-2602" />
          <DimensionBar score={score.dimensions.metadata} label="Meta" data-id="element-2603" />
          <DimensionBar score={score.dimensions.formatting} label="Fmt" data-id="element-2604" />
          <DimensionBar score={score.dimensions.tierAlignment} label="Tier" data-id="element-2605" />
        </div>
      </div>

      {isExpanded && <div className="border-t border-slate-100 p-4 bg-slate-50/50 rounded-b-xl space-y-4" data-id="element-2606">
          {hasGenericFeedback && <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl p-4" data-id="element-2607">
              <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-2608" />
              <div data-id="element-2609">
                <p className="text-sm font-bold text-rose-800" data-id="element-2610">
                  Boilerplate Explanation Detected
                </p>
                <p className="text-xs text-rose-700 mt-1" data-id="element-2611">
                  This explanation is generic and non-didactic. It does not
                  explain the concept, show the calculation, or reference the
                  specific misconception. Per Master Rulebook rule MASTER_2_2,
                  every explanation must be educational and question-specific.
                </p>
                <p className="text-xs text-rose-600 mt-2 font-medium" data-id="element-2612">
                  Required: Show the calculation steps, name the concept, and
                  explain why the wrong answers are wrong.
                </p>
              </div>
            </div>}

          {hasGenericDistractor && <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-2613">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-2614" />
              <div data-id="element-2615">
                <p className="text-sm font-bold text-amber-800" data-id="element-2616">
                  Generic Distractor Explanations
                </p>
                <p className="text-xs text-amber-700 mt-1" data-id="element-2617">
                  One or more distractor explanations are generic. Per Master
                  Rulebook rule MASTER_2_3, each wrong option must explain the
                  specific misconception it represents.
                </p>
              </div>
            </div>}

          {/* UPDATED: New dimension breakdown (20+30+25+10+10+5=100) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2618">
            <DimensionDetail title="Explanation Clarity" dimension={score.dimensions.explanationClarity} icon={BookOpen} data-id="element-2619" />
            <DimensionDetail title="Didactic Depth" dimension={score.dimensions.didacticDepth} icon={TrendingUp} data-id="element-2620" />
            <DimensionDetail title="Distractors" dimension={score.dimensions.distractors} icon={AlertTriangle} data-id="element-2621" />
            <DimensionDetail title="Metadata" dimension={score.dimensions.metadata} icon={Info} data-id="element-2622" />
            <DimensionDetail title="Formatting" dimension={score.dimensions.formatting} icon={LayoutList} data-id="element-2623" />
            <DimensionDetail title="Tier Alignment" dimension={score.dimensions.tierAlignment} icon={BarChart3} data-id="element-2624" />
          </div>

          {/* Tier alignment detail */}
          {score.tierAlignmentResult.issues.length > 0 && <div data-id="element-2625">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2626">
                Tier Alignment Issues (Band {score.tierAlignmentResult.band} ·{' '}
                {score.tierAlignmentResult.levelCode})
              </h4>
              <div className="space-y-2" data-id="element-2627">
                {score.tierAlignmentResult.issues.map((issue, idx) => <div key={idx} className={`flex items-start gap-2 text-sm p-2 rounded border ${issue.severity === 'critical' ? 'bg-rose-50 border-rose-100' : issue.severity === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`} data-id="element-2628">
                    <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${issue.severity === 'critical' ? 'text-rose-500' : issue.severity === 'warning' ? 'text-amber-500' : 'text-blue-400'}`} data-id="element-2629" />
                    <div data-id="element-2630">
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-blue-700'} data-id="element-2631">
                        {issue.message}
                      </span>
                      {issue.detail && <p className="text-xs text-slate-500 mt-0.5" data-id="element-2632">
                          {issue.detail}
                        </p>}
                      <span className="ml-2 text-xs text-slate-400 font-mono" data-id="element-2633">
                        {issue.ruleId}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}

          {score.ruleIssues.length > 0 && <div data-id="element-2634">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2635">
                Governance Violations
              </h4>
              <div className="space-y-2" data-id="element-2636">
                {score.ruleIssues.map((issue, idx) => <div key={idx} className="flex items-start gap-2 text-sm bg-white p-2 rounded border border-rose-100" data-id="element-2637">
                    <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2638" />
                    <div data-id="element-2639">
                      <span className="text-rose-700" data-id="element-2640">{issue.message}</span>
                      <span className="ml-2 text-xs text-rose-400 font-mono" data-id="element-2641">
                        {issue.rule}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}

          {score.distractorAnalysis.length > 0 && <div data-id="element-2642">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2643">
                Distractor Analysis
              </h4>
              <div className="space-y-2" data-id="element-2644">
                {score.distractorAnalysis.map((issue, idx) => <div key={idx} className="flex items-start gap-2 text-sm bg-white p-2 rounded border border-amber-100" data-id="element-2645">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2646" />
                    <span className="text-amber-700" data-id="element-2647">{issue.issue}</span>
                  </div>)}
              </div>
            </div>}
        </div>}
    </Card>;
}
function DimensionBar({
  score,
  label
}: {
  score: QualityDimension;
  label: string;
}) {
  const percent = score.score / score.maxScore * 100;
  const colorClass = percent >= 80 ? 'bg-emerald-400' : percent >= 50 ? 'bg-amber-400' : 'bg-rose-400';
  return <div className="flex-1 flex flex-col justify-end items-center gap-1 h-full group relative" data-id="element-2648">
      <div className={`w-full rounded-t-sm ${colorClass}`} style={{
      height: `${percent}%`
    }} data-id="element-2649" />
      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10" data-id="element-2650">
        {label}: {score.score}/{score.maxScore}
      </div>
    </div>;
}
function DimensionDetail({
  title,
  dimension,
  icon: Icon
}: {
  title: string;
  dimension: QualityDimension;
  icon: any;
}) {
  const percent = Math.round(dimension.score / dimension.maxScore * 100);
  const isGood = percent >= 80;
  return <div className="bg-white p-3 rounded-lg border border-slate-200" data-id="element-2651">
      <div className="flex items-center justify-between mb-2" data-id="element-2652">
        <div className="flex items-center gap-2" data-id="element-2653">
          <Icon className="w-4 h-4 text-slate-400" data-id="element-2654" />
          <span className="font-semibold text-sm text-slate-700" data-id="element-2655">{title}</span>
        </div>
        <span className={`text-xs font-bold ${isGood ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-2656">
          {dimension.score}/{dimension.maxScore}
        </span>
      </div>
      {dimension.issues.length > 0 ? <ul className="space-y-1" data-id="element-2657">
          {dimension.issues.map((issue, idx) => <li key={idx} className="text-xs text-rose-600 flex items-start gap-1.5" data-id="element-2658">
              <span className="mt-1 w-1 h-1 rounded-full bg-rose-400 shrink-0" data-id="element-2659" />
              {issue}
            </li>)}
        </ul> : <p className="text-xs text-emerald-600 flex items-center gap-1" data-id="element-2660">
          <CheckCircle2 className="w-3 h-3" data-id="element-2661" />
          No issues found
        </p>}
    </div>;
}
function getScoreColor(score: number) {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}
function getScoreBgColor(score: number) {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-rose-500';
}
function getGradeVariant(grade: string): 'success' | 'info' | 'warning' | 'danger' {
  switch (grade) {
    case 'excellent':
      return 'success';
    case 'good':
      return 'info';
    case 'needs-work':
      return 'warning';
    case 'poor':
      return 'danger';
    default:
      return 'info';
  }
}